import { v4 as uuidv4 } from "uuid";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import ClickRegionMultiSelectQuizPreview from "../../features/previews/click_region_multi_select_quiz/ClickRegionMultiSelectQuizPreview";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { Actor } from "../Actor";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import { CreateCustomClickRegion, IClickRegion } from "./ClickRegion";
import Interaction, { InteractionType } from "./Interaction";

export interface IClickRegionMultiSelectQuiz extends Interaction {
  title: string;
  clickregions: IClickRegion[];
}

export function CreateClickRegionMultiSelectQuiz(
  key: string
): IClickRegionMultiSelectQuiz {
  return {
    id: uuidv4(),
    name: "Click Region Multi Select Quiz",
    type: InteractionType.ClickRegionMultiSelectQuiz,
    title: key,
    clickregions: [
      CreateCustomClickRegion(0.4, 0.5, 0.1, 0.1),
      CreateCustomClickRegion(0.6, 0.5, 0.1, 0.1),
    ],
  };
}

export function MigrateClickRegionMultiSelectQuiz(
  from: number,
  to: number,
  interaction: IClickRegionMultiSelectQuiz
): IClickRegionMultiSelectQuiz {
  return interaction;
}

export const clickRegionMultiSelectQuizInteraction =
  (): InteractionFactoryEntity => {
    const component = <ClickRegionMultiSelectQuizPreview />;

    const create = (
      getKeys: (amount: number) => string[]
    ): IClickRegionMultiSelectQuiz => {
      const keys = getKeys(1);
      return {
        id: uuidv4(),
        name: "Click Region Multi Select Quiz",
        type: InteractionType.ClickRegionMultiSelectQuiz,
        title: keys[0],
        clickregions: [
          CreateCustomClickRegion(0.4, 0.5, 0.1, 0.1),
          CreateCustomClickRegion(0.6, 0.5, 0.1, 0.1),
        ],
      };
    };
    const migrate = (
      from: number,
      to: number,
      interaction: Interaction
    ): IClickRegionMultiSelectQuiz => {
      return interaction as IClickRegionMultiSelectQuiz;
    };

    const validate = (
      interaction: Interaction,
      getValue: (key: string) => string,
      take: Take,
      actorPresets: Actor[]
    ): string[] => {
      const converted = interaction as IClickRegionMultiSelectQuiz;
      const warnings: string[] = [];

      // Title
      ValidationHelper.ValidateKey(
        converted.title,
        getValue,
        warnings,
        "Title"
      );

      return warnings;
    };

    const copy = (
      interaction: Interaction,
      getKeys: (amount: number) => string[],
      replaceKey: (from: string, to: string) => void,
      getAnswerArray: (
        originalAnswers: IAnswer[],
        newKeys: string[]
      ) => IAnswer[]
    ): IClickRegionMultiSelectQuiz => {
      const original = interaction as IClickRegionMultiSelectQuiz;
      const [title] = getKeys(1);
      replaceKey(original.title, title);

      return {
        ...original,
        id: uuidv4(),
        title,
        clickregions: original.clickregions.map((region) => ({
          ...region,
          id: uuidv4(),
          properties: region.properties?.map((property) => ({ ...property })),
        })),
        properties: original.properties?.map((property) => ({ ...property })),
      };
    };

    const filter = (
      interaction: Interaction,
      takeName: string,
      getMatchingResults: (
        searchableStrings: string[],
        takeName: string,
        interaction: Interaction,
        filterType: string
      ) => SearchResultData[]
    ): SearchResultData[] => {
      const converted = interaction as IClickRegionMultiSelectQuiz;
      const searchableStrings: string[] = [interaction.name, converted.title];

      return getMatchingResults(
        searchableStrings,
        takeName,
        interaction,
        filterType
      );
    };

    const filterType: string = "ClickRegion MultiSelect Quiz";

    const validateErrors = (
      interaction: Interaction,
      parentTake: Take,
      currentTake: Take,
      actorPresets: Actor[]
    ): string[] => {
      const converted = interaction as IClickRegionMultiSelectQuiz;
      const errors: string[] = [];

      if (!converted.clickregions.some((region) => region.correct)) {
        errors.push("Quiz should at least have one correct answer");
      }

      return errors;
    };

    return {
      View: component,
      FilterType: filterType,
      Create: create,
      Migrate: migrate,
      Copy: copy,
      Validate: validate,
      Filter: filter,
      ValidateErrors: validateErrors,
    };
  };
